import { FieldMappingTreeNode } from './fieldMappingTreeBuilder';

/**
 * Recursively checks if a node or its descendants have any mappings
 * @param node - Tree node to check
 * @returns True if node or any descendant has a mapping
 */
export const hasAnyMappings = (node: FieldMappingTreeNode): boolean => {
  if (node.isLeaf) {
    return !!node.sourceField;
  }
  return node.children.some((child) => hasAnyMappings(child));
};

/**
 * Calculates initial expansion state for nodes with mappings
 * Uses node paths as stable row IDs instead of indices
 * @param treeData - Tree structure of destination fields
 * @returns Expanded state object for TanStack Table (always an object, never true)
 */
export const calculateInitialExpansion = (
  treeData: FieldMappingTreeNode[]
): Record<string, boolean> => {
  const expanded: Record<string, boolean> = {};

  // Recursively build expanded state using node paths
  const buildExpandedState = (nodes: FieldMappingTreeNode[]) => {
    nodes.forEach((node) => {
      if (!node.isLeaf && hasAnyMappings(node)) {
        // Use node path as row ID for stable expansion state
        expanded[node.path] = true;

        // Recursively process children
        if (node.children.length > 0) {
          buildExpandedState(node.children);
        }
      }
    });
  };

  buildExpandedState(treeData);
  return expanded;
};

/**
 * Calculates which parent nodes should be auto-expanded because they contain visible descendants
 * This is used when filtering/searching to auto-expand parents of matching nodes
 * @param treeData - Filtered tree data
 * @returns Expanded state for parent nodes (always an object, never true)
 */
export const calculateAutoExpansionForFiltering = (
  treeData: FieldMappingTreeNode[]
): Record<string, boolean> => {
  const expanded: Record<string, boolean> = {};

  // Recursively check if a node has any visible children/descendants
  const hasVisibleDescendants = (node: FieldMappingTreeNode): boolean => {
    if (node.children.length === 0) {
      return false; // Leaf node
    }
    // Has children, so it has visible descendants
    return true;
  };

  // Recursively build expanded state for parent nodes
  const buildExpandedState = (nodes: FieldMappingTreeNode[]) => {
    nodes.forEach((node) => {
      if (!node.isLeaf && hasVisibleDescendants(node)) {
        // Auto-expand this parent node
        expanded[node.path] = true;

        // Recursively process children
        if (node.children.length > 0) {
          buildExpandedState(node.children);
        }
      }
    });
  };

  buildExpandedState(treeData);
  return expanded;
};
